import React, { useCallback, useEffect, useState } from 'react';
import {
    getTaskDispute,
    markDisputeRead,
    resolveTaskDispute,
    updateDisputeStatus,
} from '../../../api/backendClient';
import DisputeMessageThread from '../../../components/dispute/DisputeMessageThread';
import { resolveAttachmentUrl } from '../../../utils/attachmentUrl';
import {
    fireErrorToast,
    fireSuccessToast,
    shortenWalletAddress,
} from '../../../utils/commonFunction';
import Loader from '../../../components/loader/Loader';
import { formatDateTime } from '../formatting';

const statusTone = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'resolved' || s === 'closed') {
        return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-300';
    }
    if (s === 'in_review') {
        return 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200';
    }
    return 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-950/40 dark:text-rose-300';
};

const TaskDisputeRoom = ({
    task,
    authToken,
    isDisputeAdmin = false,
    onResolved,
    onRefreshTask,
    initialDiscussionOpen = false,
}) => {
    const [loading, setLoading] = useState(true);
    const [dispute, setDispute] = useState(null);
    const [adminNote, setAdminNote] = useState('');
    const [resolving, setResolving] = useState(false);
    const [discussionOpen, setDiscussionOpen] = useState(initialDiscussionOpen);

    const load = useCallback(async () => {
        if (!authToken || !task?.id) return;
        try {
            setLoading(true);
            const data = await getTaskDispute(authToken, task.id);
            setDispute(data?.dispute || null);
            if (data?.dispute?.id) {
                await markDisputeRead(authToken, data.dispute.id);
            }
        } catch (e) {
            fireErrorToast(
                e?.response?.data?.message || 'Could not load dispute room',
            );
        } finally {
            setLoading(false);
        }
    }, [authToken, task?.id]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (initialDiscussionOpen && dispute?.id) {
            setDiscussionOpen(true);
        }
    }, [initialDiscussionOpen, dispute?.id]);

    const handleMarkInReview = async () => {
        if (!dispute?.id || !authToken) return;
        try {
            await updateDisputeStatus(authToken, dispute.id, 'in_review');
            fireSuccessToast('Marked in review');
            await load();
        } catch (e) {
            fireErrorToast(e?.response?.data?.message || 'Update failed');
        }
    };

    const handleResolve = async (favorEmployee) => {
        if (!authToken || !task?.id) return;
        try {
            setResolving(true);
            await resolveTaskDispute(authToken, task.id, {
                favorEmployee,
                resolutionNote: adminNote.trim(),
                resolveTxHash: null,
            });
            fireSuccessToast(
                favorEmployee
                    ? 'Recorded resolution for employee'
                    : 'Recorded resolution for company',
            );
            setAdminNote('');
            await load();
            onRefreshTask?.();
            onResolved?.();
        } catch (e) {
            fireErrorToast(
                e?.response?.data?.message ||
                    'Sync resolution after your on-chain transaction',
            );
        } finally {
            setResolving(false);
        }
    };

    const isClosed = ['resolved', 'closed'].includes(
        String(dispute?.status || '').toLowerCase(),
    );

    if (loading) {
        return (
            <div className="flex justify-center py-10">
                <Loader size="32px" />
            </div>
        );
    }

    if (!dispute) {
        return (
            <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/80 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/40">
                No dispute room yet. Raise a dispute from task actions to open
                discussion.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="overflow-hidden rounded-2xl border border-amber-200/90 bg-gradient-to-br from-amber-50/90 via-white to-white shadow-sm dark:border-amber-500/25 dark:from-amber-950/25 dark:via-zinc-950/50 dark:to-zinc-950/30">
                <div className="border-b border-amber-100/80 px-5 py-4 dark:border-amber-500/15">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                                <i className="fa-solid fa-gavel text-sm" aria-hidden />
                            </span>
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800/90 dark:text-amber-300/90">
                                    Dispute room
                                </p>
                                <h3 className="mt-0.5 truncate text-base font-semibold text-zinc-900 dark:text-white">
                                    {task?.title}
                                </h3>
                                <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                                    Raised by{' '}
                                    {shortenWalletAddress(dispute.raisedByWallet) || '—'}
                                    <span className="text-zinc-400"> · </span>
                                    {formatDateTime(dispute.createdAt)}
                                </p>
                            </div>
                        </div>
                        <span
                            className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize ${statusTone(dispute.status)}`}
                        >
                            {dispute.status?.replace('_', ' ')}
                        </span>
                    </div>
                </div>

                <div className="space-y-3 px-5 py-4">
                    {dispute.description ? (
                        <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                            {dispute.description}
                        </p>
                    ) : (
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">
                            Dispute room opened for this task. Use discussion for
                            company, employee, and dispute admin to coordinate.
                        </p>
                    )}
                    {dispute.resolutionOutcome ? (
                        <p className="rounded-lg border border-emerald-200/80 bg-emerald-50/80 px-3 py-2 text-xs font-medium text-emerald-900 dark:border-emerald-500/25 dark:bg-emerald-950/30 dark:text-emerald-200">
                            Outcome: favor{' '}
                            {dispute.resolutionOutcome === 'employee'
                                ? 'employee'
                                : 'company'}
                            {dispute.resolutionNote
                                ? ` — ${dispute.resolutionNote}`
                                : ''}
                        </p>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setDiscussionOpen((open) => !open)}
                            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-500 dark:bg-teal-600 dark:hover:bg-teal-500"
                            aria-expanded={discussionOpen}
                        >
                            <i
                                className={`fa-solid text-[11px] ${discussionOpen ? 'fa-chevron-up' : 'fa-comments'}`}
                            />
                            {discussionOpen ? 'Hide discussion' : 'View discussion'}
                        </button>
                        {dispute.filePath ? (
                            <a
                                href={resolveAttachmentUrl(dispute.filePath)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                            >
                                <i className="fa-solid fa-paperclip text-[10px]" />
                                Initial evidence
                            </a>
                        ) : null}
                    </div>
                </div>

                {isDisputeAdmin && !isClosed ? (
                    <div className="border-t border-amber-100/80 bg-amber-50/40 px-5 py-4 dark:border-amber-500/15 dark:bg-amber-950/15">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/90 dark:text-amber-200">
                            Dispute admin
                        </p>
                        <p className="mt-1 text-[11px] text-zinc-600 dark:text-zinc-400">
                            Resolve on-chain first, then record the outcome here.
                        </p>
                        <textarea
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                            rows={2}
                            placeholder="Resolution note (optional)"
                            className="cpg-input mt-3 min-h-[64px] w-full resize-y"
                        />
                        <div className="mt-3 flex flex-wrap gap-2">
                            {dispute.status === 'pending' ? (
                                <button
                                    type="button"
                                    onClick={handleMarkInReview}
                                    className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    Mark in review
                                </button>
                            ) : null}
                            <button
                                type="button"
                                disabled={resolving}
                                onClick={() => handleResolve(true)}
                                className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                                Record → Employee
                            </button>
                            <button
                                type="button"
                                disabled={resolving}
                                onClick={() => handleResolve(false)}
                                className="rounded-lg bg-zinc-800 px-3 py-2 text-xs font-semibold text-white hover:bg-zinc-900 disabled:opacity-50 dark:bg-zinc-700"
                            >
                                Record → Company
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>

            {discussionOpen ? (
                <DisputeMessageThread
                    disputeId={dispute.id}
                    authToken={authToken}
                    variant="jobs"
                    readOnly={isClosed}
                    pollIntervalMs={12000}
                />
            ) : null}
        </div>
    );
};

export default TaskDisputeRoom;
